import { useEffect, useState, useMemo } from "react";
import { fetchSpecs } from "@/lib/api";
import { Section } from "@/components/CalcUI";
import { Search } from "lucide-react";

const KINDS = [
  { key: "casing", label: "Casing" },
  { key: "tubing", label: "Tubing" },
  { key: "drill-pipe", label: "Drill Pipe" },
];

const COLS = {
  casing: [
    { k: "od", label: "OD (in)" },
    { k: "weight", label: "Weight (lb/ft)" },
    { k: "wall", label: "Wall (in)" },
    { k: "id", label: "ID (in)" },
    { k: "drift", label: "Drift (in)" },
    { k: "grade", label: "Grade" },
    { k: "yield", label: "Yield (psi)" },
    { k: "burst", label: "Burst (psi)" },
    { k: "collapse", label: "Collapse (psi)" },
  ],
  tubing: [
    { k: "od", label: "OD (in)" },
    { k: "weight", label: "Weight (lb/ft)" },
    { k: "wall", label: "Wall (in)" },
    { k: "id", label: "ID (in)" },
    { k: "drift", label: "Drift (in)" },
    { k: "grade", label: "Grade" },
    { k: "yield", label: "Yield (psi)" },
    { k: "burst", label: "Burst (psi)" },
    { k: "collapse", label: "Collapse (psi)" },
  ],
  "drill-pipe": [
    { k: "od", label: "OD (in)" },
    { k: "weight", label: "Weight (lb/ft)" },
    { k: "wall", label: "Wall (in)" },
    { k: "id", label: "ID (in)" },
    { k: "grade", label: "Grade" },
    { k: "yield", label: "Yield (psi)" },
    { k: "capacity_bbl_ft", label: "Cap (bbl/ft)" },
  ],
};

export default function SpecLibrary() {
  const [kind, setKind] = useState("casing");
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchSpecs(kind).then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [kind]);

  const filtered = useMemo(() => {
    if (!q.trim()) return data;
    const needle = q.trim().toLowerCase();
    return data.filter((r) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [data, q]);

  const cols = COLS[kind];

  return (
    <div data-testid="specs-page">
      <Section title="Casing / Tubing / Drill Pipe Library" subtitle="Standard API pipe specs — OD, weight, wall, drift, grade, yield, and pressure ratings. Cross-reference before any tally or pull test.">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="inline-flex border border-[#334155]">
            {KINDS.map((k) => (
              <button
                key={k.key}
                data-testid={`spec-tab-${k.key}`}
                onClick={() => setKind(k.key)}
                className={`px-4 py-2 text-xs mono tracking-widest uppercase transition-colors ${
                  kind === k.key ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-[#0b0e13] border border-[#334155] focus-within:border-[#f59e0b] px-3">
            <Search className="w-4 h-4 text-[#64748b]" />
            <input
              data-testid="spec-search"
              placeholder="filter by OD, grade..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-transparent py-2 text-sm mono focus:outline-none"
            />
          </div>
        </div>

        <div className="border border-[#334155] overflow-x-auto">
          <table className="w-full mono text-sm">
            <thead>
              <tr className="border-b border-[#334155] bg-[#0b0e13] text-xs uppercase tracking-widest text-[#94a3b8]">
                {cols.map((c) => (
                  <th key={c.k} className="text-right px-4 py-2 first:text-left">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={cols.length} className="text-center py-6 text-[#94a3b8]">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={cols.length} className="text-center py-6 text-[#94a3b8]">No matches</td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr
                    key={JSON.stringify(r)}
                    data-testid={`spec-row-${i}`}
                    className={i % 2 === 0 ? "bg-[#0b0e13]" : "bg-[#11151c]"}
                  >
                    {cols.map((c) => (
                      <td key={c.k} className="text-right px-4 py-2 first:text-left text-[#f8fafc]">
                        {r[c.k] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
